/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { ReactNode } from 'react';
import { ProviderIntegrationStatus, ProviderSetupInfoRequired } from '@g17eco/types/app-integration';
import { SetupView } from './setup/SetupView';
import { PurchaseRequiredView } from './setup/PurchaseRequiredView';
import { Button } from 'reactstrap';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { toastFromError } from '@g17eco/molecules/toasts';
import WorkingOnIt from '../../images/workingonit.gif';
import { BasicAlert } from '@g17eco/molecules/alert';
interface Props extends ProviderSetupInfoRequired {
  initiativeId: string;
}

const BasicIntegrationView = (props: { children: ReactNode, icon?: ReactNode }) => {
  return (
    <div>
      {props.children}
      <div className={'text-center'}>
        {props.icon}
      </div>
    </div>
  )
}

export const IntegrationProcessView = (props: Props) => {

  switch (props.status) {
    case ProviderIntegrationStatus.Active: {
      const login = props.externalApp?.login;
      return (
        <BasicIntegrationView>
          <h5>Active Connection is enabled</h5>
          <div className={'p-4 text-center'}>
            <i className='fa-solid text-success fa-8x fa-circle-check' />
          </div>

          {
            login ? (
              <div className={'text-center'}>
                <Button color={'primary'} size={'lg'} onClick={() => {
                  window.open(login.url, '_blank', '');
                }}>
                  {login.text ?? `Go to ${props.provider.name}`}
                </Button>
              </div>
            ) : null
          }

        </BasicIntegrationView>
      )
    }
    case ProviderIntegrationStatus.Error: {
      return (
        <BasicIntegrationView icon={<i className='fa-solid fa-2x fa-circle-check' />}>
          <BasicAlert type={'danger'}>
            An error have occured during the setup process. Please contact support.
          </BasicAlert>
        </BasicIntegrationView>
      )
    }
    case ProviderIntegrationStatus.Pending: {
      return (
        <BasicIntegrationView>
          <h4 className={'my-4'}>
            Your Integration is currently pending and will be notified once it is completed.
          </h4>
          <div className={'text-center p-sm-3'}>
            <div style={{ maxWidth: '500px' }} className={'d-inline-block'}>
              <img src={WorkingOnIt} alt='Work in Progress' />
            </div>
          </div>
        </BasicIntegrationView>
      )
    }
  }

  if (!props.integration.requirements.questions.length) {
    if (props.provider.requiresProduct) {
      return (
        <PurchaseRequiredView
          initiativeId={props.initiativeId}
          requiresProduct={props.provider.requiresProduct}
          providerName={props.provider.name}
          onCreate={props.onCreate}
        />
      );
    }

    return (
      <div className={'text-center'}>
        <SubmitButton color={'primary'} size={'lg'} onClick={() =>
          props.onCreate({ generatedAnswers: [] })
            .catch((error) => {
              toastFromError({ error, title: 'Failed to enable integration' });
            })
        }>
          Enable integration
        </SubmitButton>
      </div>
    );
  }

  return <SetupView {...props} />
}
